import * as React from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";

import { cn } from "@/lib/utils";

/**
 * Slider component displays a range input slider for selecting numeric values.
 * Built on Radix UI Slider primitive with custom styling. Supports min, max, step, and value props.
 * 
 * @component
 * @param {React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root>} props - Slider props
 * @param {number[]} props.value - Current slider value(s) as array
 * @param {(value: number[]) => void} props.onValueChange - Callback fired when value changes
 * @param {number} [props.min=0] - Minimum value
 * @param {number} [props.max=100] - Maximum value
 * @param {number} [props.step=1] - Step increment
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} Slider control with track, range, and thumb
 * 
 * @example
 * ```tsx
 * <Slider value={[50]} onValueChange={(v) => setValue(v[0])} min={0} max={100} />
 * ```
 */
const Slider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root>
>(({ className, ...props }, ref) => (
  <SliderPrimitive.Root
    ref={ref}
    className={cn(
      "relative flex w-full touch-none select-none items-center",
      className
    )}
    {...props}
  >
    <SliderPrimitive.Track className="relative h-1.5 w-full grow overflow-hidden rounded-full bg-[#E9ECEF]">
      <SliderPrimitive.Range className="absolute h-full bg-[#005587]" />
    </SliderPrimitive.Track>
    <SliderPrimitive.Thumb className="block h-4 w-4 rounded-full bg-[#005587] shadow transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50" />
  </SliderPrimitive.Root>
));
Slider.displayName = SliderPrimitive.Root.displayName;

export { Slider };
